import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const DEFAULT_THEME_COLOR = '#f97316';
const DARK_NOTCH_COLOR = '#000000';

const applyAppleStatusBarStyle = (darkNotchEnabled: boolean) => {
  let appleMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
  if (!appleMeta) {
    appleMeta = document.createElement('meta');
    appleMeta.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
    document.head.appendChild(appleMeta);
  }

  const nextStyle = darkNotchEnabled ? 'black-translucent' : 'default';
  if (appleMeta.getAttribute('content') !== nextStyle) {
    appleMeta.setAttribute('content', nextStyle);
  }
};

const readStoredDarkNotch = (): boolean => {
  const keys = ['appearance-settings', 'guest-appearance-settings'];
  for (const key of keys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.darkNotch === 'boolean') {
        return parsed.darkNotch;
      }
    } catch {
      // Ignore malformed storage
    }
  }
  return false;
};

/**
 * Hook to dynamically update the browser's theme-color meta tag
 * based on the current route and theme settings
 */
export function useThemeColor() {
  const location = useLocation();

  useEffect(() => {
    let lastAppliedColor = '';

    const updateThemeColor = () => {
      const darkNotchEnabled = readStoredDarkNotch();

      if (darkNotchEnabled) {
        if (lastAppliedColor === DARK_NOTCH_COLOR) {
          applyAppleStatusBarStyle(true);
          return;
        }

        lastAppliedColor = DARK_NOTCH_COLOR;

        const forcedMetas = document.querySelectorAll('meta[name="theme-color"]');
        if (forcedMetas.length > 0) {
          forcedMetas.forEach(meta => {
            meta.setAttribute('content', DARK_NOTCH_COLOR);
          });
        } else {
          const meta = document.createElement('meta');
          meta.name = 'theme-color';
          meta.content = DARK_NOTCH_COLOR;
          document.head.appendChild(meta);
        }
        applyAppleStatusBarStyle(true);
        return;
      }

      // Always use the primary color from CSS variables
      const rootStyles = getComputedStyle(document.documentElement);
      const primaryHSL = rootStyles.getPropertyValue('--primary').trim();
      const isDarkMode = document.documentElement.classList.contains('dark');

      let nextColor = DEFAULT_THEME_COLOR;
      if (primaryHSL) {
        const hslValues = primaryHSL.split(' ').map(v => parseFloat(v));
        if (hslValues.length === 3 && hslValues.every(v => Number.isFinite(v))) {
          const [h, s, l] = hslValues;
          const adjustedL = isDarkMode ? Math.max(15, l * 0.7) : l;
          nextColor = hslToHex(h, s, adjustedL);
        }
      }

      if (lastAppliedColor === nextColor) {
        return;
      }

      lastAppliedColor = nextColor;

      // Keep a stable set of tags and only update content to prevent status bar flicker.
      const themeMetas = document.querySelectorAll('meta[name="theme-color"]');
      if (themeMetas.length > 0) {
        themeMetas.forEach(meta => {
          meta.setAttribute('content', nextColor);
        });
      } else {
        const meta = document.createElement('meta');
        meta.name = 'theme-color';
        meta.content = nextColor;
        document.head.appendChild(meta);
      }
      applyAppleStatusBarStyle(false);
    };

    // Update immediately
    updateThemeColor();
    
    // Also update after a small delay to ensure CSS vars are loaded
    const immediateTimeout = setTimeout(updateThemeColor, 100);

    // Only observe class changes to avoid loops from style mutations.
    const observer = new MutationObserver(() => {
      updateThemeColor();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Listen for custom event when appearance settings change
    const handleAppearanceChange = () => {
      setTimeout(updateThemeColor, 50); // Small delay to ensure CSS vars are updated
    };
    
    window.addEventListener('appearance-updated', handleAppearanceChange);

    return () => {
      clearTimeout(immediateTimeout);
      observer.disconnect();
      window.removeEventListener('appearance-updated', handleAppearanceChange);
    };
  }, [location.pathname]);
}

/**
 * Convert HSL to Hex color
 */
function hslToHex(h: number, s: number, l: number): string {
  s = s / 100;
  l = l / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  
  let r = 0, g = 0, b = 0;

  if (0 <= h && h < 60) {
    r = c; g = x; b = 0;
  } else if (60 <= h && h < 120) {
    r = x; g = c; b = 0;
  } else if (120 <= h && h < 180) {
    r = 0; g = c; b = x;
  } else if (180 <= h && h < 240) {
    r = 0; g = x; b = c;
  } else if (240 <= h && h < 300) {
    r = x; g = 0; b = c;
  } else if (300 <= h && h < 360) {
    r = c; g = 0; b = x;
  }

  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
